import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Tab, Tabs, Badge, Alert, ListGroup, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';

import adminService from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';

const Settings: React.FC = () => {
  const { hasPermission } = useAuth();
  const canWriteSettings = hasPermission('settings:write');
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
const [loadingEmail, setLoadingEmail] = useState(false);
const [loadingConnection, setLoadingConnection] = useState(false);
  const [settings, setSettings] = useState({
    // General Settings
    siteName: 'MyCinemas',
    siteDescription: 'Kerala\'s Premier Cinema Experience',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    language: 'en',
    contactEmail: 'support@mycinemas.com',
    contactPhone: '+91-484-1234567',
    address: 'MG Road, Kochi, Kerala 682001',
    
    // Booking Settings
    maxSeatsPerBooking: 8,
    bookingTimeout: 15,
    cancellationDeadline: 2,
    refundProcessingDays: 3,
    enableSeatSelection: true,
    enableAdvanceBooking: true,
    advanceBookingDays: 30,
    
    // Payment Settings
    paymentGateway: 'razorpay',
    razorpayKeyId: 'rzp_test_1234567890',
    razorpayKeySecret: '••••••••••••••••••••••••••••••••',
    easebuzzKey: '',
    easebuzzSalt: '',
    easebuzzEnv: 'test',
    easebuzzIframe: true,
    easebuzzWebhookSecret: '',
    enableUPI: true,
    enableCards: true,
    enableNetBanking: true,
    enableWallet: true,
    enableCashOnDelivery: false,
    paymentTimeout: 10,
    
    // Convenience Fee Settings
    enableConvenienceFee: false,
    convenienceFeeType: 'fixed',
    convenienceFeeValue: 0,
    
    // Vista Integration Settings
    vistaApiUrl: 'https://api.vista.com/v1',
    vistaApiKey: '••••••••••••••••••••••••••••••••',
    vistaApiSecret: '••••••••••••••••••••••••••••••••',
    enableVistaSync: true,
    vistaSyncInterval: 30,
    enableVistaBooking: true,
    enableVistaInventory: true,
    vistaTimeout: 30,
    
    // Cinema Settings
    enableOnlineBooking: true,
    enableWalkInBooking: true,
    enableFoodOrdering: true,
    enableLoyaltyProgram: true,
    loyaltyPointsPerRupee: 1,
    enableMembership: true,
    membershipDiscount: 10,
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    bookingConfirmations: true,
    paymentReminders: true,
    showReminders: true,
    cancellationNotifications: true,
    promotionalEmails: true,
    
    // Security Settings
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    enable2FA: false,
    passwordExpiry: 90,
    enableIpWhitelist: false,
    allowedIpAddresses: '',
    
    // Maintenance Settings
    maintenanceMode: false,
    maintenanceMessage: 'We are currently performing scheduled maintenance. Please check back soon.',
    enableLogging: true,
    logLevel: 'info',
    enableBackup: true,
    backupFrequency: 'daily',
    
    // Brevo Integration Settings
    brevoApiKey: '',
    brevoSenderEmail: 'noreply@mycinemas.com',
    brevoSenderName: 'MyCinemas',
    whatsappBusinessNumber: '+918484123456',
    whatsappBusinessName: 'MyCinemas',
    
    // Zepto Integration Settings
    zeptoApiKey: '',
    zeptoSenderEmail: 'noreply@mycinemas.in',
    zeptoSenderName: 'MyCinemas',
    
    // F&B Settings
    fnbGst: 5,

    smsApiKey: '',
    whatsappApiKey:'',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await adminService.getSettings();

        if (response.success && response.data) {
          setSettings(prev => ({
            ...prev,
            ...response.data,
          }));
        } else {
          toast.error(response.error || 'Failed to load settings');
        }
      } catch (error: any) {
        toast.error(error.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleInputChange = (key: string, value: any) => {
    if (!canWriteSettings) {
      toast.error('You do not have permission to modify settings');
      return;
    }
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    if (!canWriteSettings) {
      toast.error('You do not have permission to save settings');
      return;
    }

    try {
      setSaving(true);
      const response = await adminService.updateSettings(settings);

      if (response.success) {
        toast.success(response.message || 'Settings saved successfully');
        toast.success('Changes will be reflected on the website instantly');
        
        // Trigger instant refresh on website via localStorage event
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            const timestamp = Date.now().toString();
            window.localStorage.setItem('admin_settings_updated', timestamp);
            // Also trigger cross-tab communication
            window.dispatchEvent(new StorageEvent('storage', {
              key: 'admin_settings_updated',
              newValue: timestamp,
              url: window.location.href
            }));
          }
        }, 500);
      } else {
        toast.error(response.error || 'Failed to save settings');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoadingConnection(true);
  
      const res = await adminService.testZeptoConnection();
  
      if (res.success) {
        alert('✅ Zepto connection successful');
      } else {
        alert('❌ Zepto connection failed');
      }
    } catch (err) {
      alert('❌ Error testing connection');
    } finally {
      setLoadingConnection(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    try {
      if (!testEmail) {
        alert('Please enter an email');
        return;
      }
  
      setLoadingEmail(true);
  
      const res = await adminService.sendTestZeptoEmail(testEmail);
  
      if (res.success) {
        alert('✅ Test email sent');
      } else {
        alert('❌ Failed to send email');
      }
    } catch (err) {
      alert('❌ Error sending email');
    } finally {
      setLoadingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status" className="me-2">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Settings - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Settings</h2>
            <p className="text-muted mb-0">Configure system settings and preferences</p>
            <small className="text-success">
              <i className="bi-info-circle me-1"></i>
              Changes are automatically reflected on the website within 5 minutes
            </small>
          </div>
          {canWriteSettings && (
            <Button 
              variant="primary" 
              onClick={handleSave} 
              disabled={saving}
            >
              <i className="bi-save me-1"></i>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
        {!canWriteSettings && (
          <Alert variant="warning" className="mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            You do not have permission to modify settings. Please contact an administrator.
          </Alert>
        )}

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'general')} className="mb-4" variant="pills">
          <Tab eventKey="general" title="General">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">General Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Site Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.siteName}
                        onChange={(e) => handleInputChange('siteName', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Timezone</Form.Label>
                      <Form.Select
                        value={settings.timezone}
                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                      >
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">America/New_York (EST)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Currency</Form.Label>
                      <Form.Select
                        value={settings.currency}
                        onChange={(e) => handleInputChange('currency', e.target.value)}
                      >
                        <option value="INR">Indian Rupee (₹)</option>
                        <option value="USD">US Dollar ($)</option>
                        <option value="EUR">Euro (€)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Default Language</Form.Label>
                      <Form.Select
                        value={settings.language}
                        onChange={(e) => handleInputChange('language', e.target.value)}
                      >
                        <option value="en">English</option>
                        <option value="ml">Malayalam</option>
                        <option value="hi">Hindi</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Site Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={settings.siteDescription}
                    onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                  />
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={settings.contactEmail}
                        onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        value={settings.contactPhone}
                        onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={settings.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="booking" title="Booking">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Booking Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Seats Per Booking</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="10"
                        value={settings.maxSeatsPerBooking}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value > 10) {
                            toast.error('Maximum seats per booking cannot exceed 10');
                            return;
                          }
                          handleInputChange('maxSeatsPerBooking', value);
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Booking Timeout (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="60"
                        value={settings.bookingTimeout}
                        onChange={(e) => handleInputChange('bookingTimeout', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cancellation Deadline (hours before show)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="24"
                        value={settings.cancellationDeadline}
                        onChange={(e) => handleInputChange('cancellationDeadline', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Refund Processing (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="30"
                        value={settings.refundProcessingDays}
                        onChange={(e) => handleInputChange('refundProcessingDays', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <hr className="my-4" />
                
                <h6 className="mb-3">F&B Settings</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>F&B GST (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="50"
                        step="0.1"
                        value={settings.fnbGst}
                        onChange={(e) => handleInputChange('fnbGst', parseFloat(e.target.value) || 0)}
                      />
                      <Form.Text className="text-muted">
                        GST percentage applied to Food & Beverage items
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="payment" title="Payment">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Payment Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Gateway</Form.Label>
                      <Form.Select
                        value={settings.paymentGateway}
                        onChange={(e) => handleInputChange('paymentGateway', e.target.value)}
                      >
                        <option value="razorpay">Razorpay</option>
                        <option value="easebuzz">Easebuzz</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Timeout (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="30"
                        value={settings.paymentTimeout}
                        onChange={(e) => handleInputChange('paymentTimeout', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                {settings.paymentGateway === 'razorpay' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Razorpay Key ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={settings.razorpayKeyId}
                          onChange={(e) => handleInputChange('razorpayKeyId', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Razorpay Key Secret</Form.Label>
                        <Form.Control
                          type="password"
                          value={settings.razorpayKeySecret}
                          onChange={(e) => handleInputChange('razorpayKeySecret', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {settings.paymentGateway === 'easebuzz' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Easebuzz Key</Form.Label>
                        <Form.Control
                          type="text"
                          value={settings.easebuzzKey}
                          onChange={(e) => handleInputChange('easebuzzKey', e.target.value)}
                          placeholder="Enter Easebuzz Key"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Easebuzz Salt</Form.Label>
                        <Form.Control
                          type="password"
                          value={settings.easebuzzSalt}
                          onChange={(e) => handleInputChange('easebuzzSalt', e.target.value)}
                          placeholder="Enter Easebuzz Salt"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Easebuzz Environment</Form.Label>
                        <Form.Select
                          value={settings.easebuzzEnv}
                          onChange={(e) => handleInputChange('easebuzzEnv', e.target.value)}
                        >
                          <option value="test">Test</option>
                          <option value="prod">Production</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Easebuzz Webhook Secret (Optional)</Form.Label>
                        <Form.Control
                          type="password"
                          value={settings.easebuzzWebhookSecret}
                          onChange={(e) => handleInputChange('easebuzzWebhookSecret', e.target.value)}
                          placeholder="Enter Webhook Secret (optional)"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="easebuzzIframe"
                          label="Enable iFrame Mode (easeCheckout)"
                          checked={settings.easebuzzIframe}
                          onChange={(e) => handleInputChange('easebuzzIframe', e.target.checked)}
                        />
                        <Form.Text className="text-muted">
                          Enable iFrame mode for seamless payment experience. If disabled, uses redirect mode.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                
                <h6 className="mb-3">Payment Methods</h6>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableUPI"
                      label="Enable UPI"
                      checked={settings.enableUPI}
                      onChange={(e) => handleInputChange('enableUPI', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="enableCards"
                      label="Enable Credit/Debit Cards"
                      checked={settings.enableCards}
                      onChange={(e) => handleInputChange('enableCards', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableNetBanking"
                      label="Enable Net Banking"
                      checked={settings.enableNetBanking}
                      onChange={(e) => handleInputChange('enableNetBanking', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="enableWallet"
                      label="Enable Digital Wallets"
                      checked={settings.enableWallet}
                      onChange={(e) => handleInputChange('enableWallet', e.target.checked)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableCashOnDelivery"
                      label="Enable Cash on Delivery"
                      checked={settings.enableCashOnDelivery}
                      onChange={(e) => handleInputChange('enableCashOnDelivery', e.target.checked)}
                    />
                  </Col>
                </Row>
                
                <hr className="my-4" />
                
                <h6 className="mb-3">Convenience Fee Settings</h6>
                <Row>
                  <Col md={12}>
                    <Form.Check
                      type="switch"
                      id="enableConvenienceFee"
                      label="Enable Convenience Fee"
                      checked={settings.enableConvenienceFee}
                      onChange={(e) => handleInputChange('enableConvenienceFee', e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      Convenience fee is charged per seat. GST will be applied to this fee.
                    </Form.Text>
                  </Col>
                </Row>
                
                {settings.enableConvenienceFee && (
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Fee Type</Form.Label>
                        <Form.Select
                          value={settings.convenienceFeeType}
                          onChange={(e) => handleInputChange('convenienceFeeType', e.target.value)}
                        >
                          <option value="fixed">Fixed Amount (per seat)</option>
                          <option value="percentage">Percentage (of seat price)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {settings.convenienceFeeType === 'percentage' 
                            ? 'Fee Percentage (%)' 
                            : 'Fee Amount (₹ per seat)'}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step={settings.convenienceFeeType === 'percentage' ? '0.01' : '1'}
                          value={settings.convenienceFeeValue}
                          onChange={(e) => handleInputChange('convenienceFeeValue', parseFloat(e.target.value) || 0)}
                        />
                        <Form.Text className="text-muted">
                          {settings.convenienceFeeType === 'percentage' 
                            ? 'Percentage of seat price (e.g., 2.5 for 2.5%)' 
                            : 'Fixed amount in ₹ per seat (e.g., 20 for ₹20 per seat)'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* <Tab eventKey="notifications" title="Notifications">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Notification Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6 className="mb-3">Notification Channels</h6>
                    <Form.Check
                      type="switch"
                      id="emailNotifications"
                      label="Email Notifications"
                      checked={settings.emailNotifications}
                      onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="smsNotifications"
                      label="SMS Notifications"
                      checked={settings.smsNotifications}
                      onChange={(e) => handleInputChange('smsNotifications', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="pushNotifications"
                      label="Push Notifications"
                      checked={settings.pushNotifications}
                      onChange={(e) => handleInputChange('pushNotifications', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <h6 className="mb-3">Notification Types</h6>
                    <Form.Check
                      type="switch"
                      id="bookingConfirmations"
                      label="Booking Confirmations"
                      checked={settings.bookingConfirmations}
                      onChange={(e) => handleInputChange('bookingConfirmations', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="paymentReminders"
                      label="Payment Reminders"
                      checked={settings.paymentReminders}
                      onChange={(e) => handleInputChange('paymentReminders', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="showReminders"
                      label="Show Reminders"
                      checked={settings.showReminders}
                      onChange={(e) => handleInputChange('showReminders', e.target.checked)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="cancellationNotifications"
                      label="Cancellation Notifications"
                      checked={settings.cancellationNotifications}
                      onChange={(e) => handleInputChange('cancellationNotifications', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="promotionalEmails"
                      label="Promotional Emails"
                      checked={settings.promotionalEmails}
                      onChange={(e) => handleInputChange('promotionalEmails', e.target.checked)}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab> */}

          {/* <Tab eventKey="security" title="Security">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Security Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Session Timeout (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        min="15"
                        max="480"
                        value={settings.sessionTimeout}
                        onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Login Attempts</Form.Label>
                      <Form.Control
                        type="number"
                        min="3"
                        max="10"
                        value={settings.maxLoginAttempts}
                        onChange={(e) => handleInputChange('maxLoginAttempts', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enable2FA"
                      label="Enable Two-Factor Authentication"
                      checked={settings.enable2FA}
                      onChange={(e) => handleInputChange('enable2FA', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password Expiry (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="30"
                        max="365"
                        value={settings.passwordExpiry}
                        onChange={(e) => handleInputChange('passwordExpiry', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableIpWhitelist"
                      label="Enable IP Whitelist"
                      checked={settings.enableIpWhitelist}
                      onChange={(e) => handleInputChange('enableIpWhitelist', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Allowed IP Addresses</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={settings.allowedIpAddresses}
                        onChange={(e) => handleInputChange('allowedIpAddresses', e.target.value)}
                        disabled={!settings.enableIpWhitelist}
                        placeholder="192.168.1.1&#10;10.0.0.1&#10;172.16.0.1"
                      />
                      <Form.Text className="text-muted">
                        Enter one IP address per line
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab> */}

          {/* <Tab eventKey="vista" title="Vista Integration">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Vista Integration Settings</h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="info">
                  <i className="bi-info-circle me-2"></i>
                  <strong>Vista Integration:</strong> Connect with Vista cinema management system for real-time inventory and booking synchronization.
                </Alert>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vista API URL</Form.Label>
                      <Form.Control
                        type="url"
                        value={settings.vistaApiUrl}
                        onChange={(e) => handleInputChange('vistaApiUrl', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>API Key</Form.Label>
                      <Form.Control
                        type="password"
                        value={settings.vistaApiKey}
                        onChange={(e) => handleInputChange('vistaApiKey', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>API Secret</Form.Label>
                      <Form.Control
                        type="password"
                        value={settings.vistaApiSecret}
                        onChange={(e) => handleInputChange('vistaApiSecret', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sync Interval (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="120"
                        value={settings.vistaSyncInterval}
                        onChange={(e) => handleInputChange('vistaSyncInterval', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>API Timeout (seconds)</Form.Label>
                      <Form.Control
                        type="number"
                        min="10"
                        max="120"
                        value={settings.vistaTimeout}
                        onChange={(e) => handleInputChange('vistaTimeout', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <h6 className="mb-3">Integration Features</h6>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableVistaSync"
                      label="Enable Vista Synchronization"
                      checked={settings.enableVistaSync}
                      onChange={(e) => handleInputChange('enableVistaSync', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="enableVistaBooking"
                      label="Enable Vista Booking Integration"
                      checked={settings.enableVistaBooking}
                      onChange={(e) => handleInputChange('enableVistaBooking', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableVistaInventory"
                      label="Enable Vista Inventory Sync"
                      checked={settings.enableVistaInventory}
                      onChange={(e) => handleInputChange('enableVistaInventory', e.target.checked)}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab> */}

          {/* <Tab eventKey="cinema" title="Cinema">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Cinema Settings</h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Booking Options</h6>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableOnlineBooking"
                      label="Enable Online Booking"
                      checked={settings.enableOnlineBooking}
                      onChange={(e) => handleInputChange('enableOnlineBooking', e.target.checked)}
                    />
                    <Form.Check
                      type="switch"
                      id="enableWalkInBooking"
                      label="Enable Walk-in Booking"
                      checked={settings.enableWalkInBooking}
                      onChange={(e) => handleInputChange('enableWalkInBooking', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableFoodOrdering"
                      label="Enable Food Ordering"
                      checked={settings.enableFoodOrdering}
                      onChange={(e) => handleInputChange('enableFoodOrdering', e.target.checked)}
                    />
                  </Col>
                </Row>
                
                <h6 className="mb-3 mt-4">Loyalty Program</h6>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableLoyaltyProgram"
                      label="Enable Loyalty Program"
                      checked={settings.enableLoyaltyProgram}
                      onChange={(e) => handleInputChange('enableLoyaltyProgram', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Points Per Rupee</Form.Label>
                      <Form.Control
                        type="number"
                        min="0.1"
                        max="10"
                        step="0.1"
                        value={settings.loyaltyPointsPerRupee}
                        onChange={(e) => handleInputChange('loyaltyPointsPerRupee', parseFloat(e.target.value))}
                        disabled={!settings.enableLoyaltyProgram}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <h6 className="mb-3 mt-4">Membership</h6>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="enableMembership"
                      label="Enable Membership Program"
                      checked={settings.enableMembership}
                      onChange={(e) => handleInputChange('enableMembership', e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Membership Discount (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="50"
                        value={settings.membershipDiscount}
                        onChange={(e) => handleInputChange('membershipDiscount', parseInt(e.target.value))}
                        disabled={!settings.enableMembership}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab> */}

          {/* <Tab eventKey="brevo" title="Brevo Integration">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Brevo (Transactional Notifications)</h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="info">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="d-flex align-items-center">
                        <i className="bi-info-circle fs-4 me-3"></i>
                        <div>
                          <strong>About Brevo Integration</strong>
                          <p className="mb-0 mt-1">
                            <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer">Brevo</a> (formerly Sendinblue) provides professional email, SMS, and WhatsApp delivery for transactional notifications including booking confirmations, payment receipts, and show reminders.
                          </p>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <Button variant="outline-primary" size="sm" href="https://app.brevo.com/settings/keys/api" target="_blank">
                        <i className="bi-box-arrow-up-right me-1"></i>Get API Key
                      </Button>
                    </Col>
                  </Row>
                </Alert>

                <h6 className="mb-3">API Configuration</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Brevo API Key *</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxxxxxxxxxx-xxxxxxxxxxxxxxxx"
                        value={settings.brevoApiKey}
                        onChange={(e) => handleInputChange('brevoApiKey', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Get your API key from <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer">Brevo Dashboard</a>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-3">Sender Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sender Email *</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="noreply@mycinemas.com"
                        value={settings.brevoSenderEmail}
                        onChange={(e) => handleInputChange('brevoSenderEmail', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Must be verified in Brevo
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sender Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="MyCinemas"
                        value={settings.brevoSenderName}
                        onChange={(e) => handleInputChange('brevoSenderName', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-3">WhatsApp Business</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Business Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="+918484123456"
                        value={settings.whatsappBusinessNumber}
                        onChange={(e) => handleInputChange('whatsappBusinessNumber', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        WhatsApp Business number registered with Brevo
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Business Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="MyCinemas"
                        value={settings.whatsappBusinessName}
                        onChange={(e) => handleInputChange('whatsappBusinessName', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-3">Transactional Templates</h6>
                <Alert variant="warning">
                  <i className="bi-exclamation-triangle me-2"></i>
                  WhatsApp templates must be pre-approved by WhatsApp. Create and submit templates in your <a href="https://app.brevo.com/whatsapp/template" target="_blank" rel="noopener noreferrer">Brevo WhatsApp Dashboard</a>.
                </Alert>
                
                <ListGroup className="mb-3">
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-medium">booking_confirmation</div>
                      <small className="text-muted">Sent when user completes a booking</small>
                    </div>
                    <Badge bg="success">Active</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-medium">payment_confirmation</div>
                      <small className="text-muted">Sent after successful payment</small>
                    </div>
                    <Badge bg="success">Active</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-medium">show_reminder</div>
                      <small className="text-muted">Sent 2 hours before show time</small>
                    </div>
                    <Badge bg="success">Active</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-medium">booking_cancellation</div>
                      <small className="text-muted">Sent when booking is cancelled</small>
                    </div>
                    <Badge bg="success">Active</Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-medium">promotional_offer</div>
                      <small className="text-muted">Marketing campaigns and special offers</small>
                    </div>
                    <Badge bg="warning">Pending Approval</Badge>
                  </ListGroup.Item>
                </ListGroup>

                <h6 className="mb-3">Connection Test</h6>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary">
                    <i className="bi-plug me-1"></i>Test Connection
                  </Button>
                  <Button variant="outline-success">
                    <i className="bi-whatsapp me-1"></i>Send Test WhatsApp
                  </Button>
                  <Button variant="outline-info">
                    <i className="bi-envelope me-1"></i>Send Test Email
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Tab> */}

          <Tab eventKey="zepto" title="Zepto Integration">
            <Card className="mt-3">
              <Card.Header>
                <h5 className="mb-0">Zepto (Transactional Notifications)</h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="info">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="d-flex align-items-center">
                        <i className="bi-info-circle fs-4 me-3"></i>
                        <div>
                          <strong>About Zepto Integration</strong>
                          <p className="mb-0 mt-1">
                            <a href="https://www.zoho.com/zeptomail/" target="_blank" rel="noopener noreferrer">ZeptoMail</a> provides professional email delivery for transactional notifications including booking confirmations, payment receipts, and show reminders.
                          </p>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <Button variant="outline-primary" size="sm" href="https://www.zoho.com/zeptomail/" target="_blank">
                        <i className="bi-box-arrow-up-right me-1"></i>Get API Key
                      </Button>
                    </Col>
                  </Row>
                </Alert>

                <h6 className="mb-3">API Configuration</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zepto API Key *</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Zoho-enczapikey xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        value={settings.zeptoApiKey}
                        onChange={(e) => handleInputChange('zeptoApiKey', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Get your API key from <a href="https://www.zoho.com/zeptomail/" target="_blank" rel="noopener noreferrer">ZeptoMail Dashboard</a>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-3">Sender Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sender Email *</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="noreply@mycinemas.in"
                        value={settings.zeptoSenderEmail}
                        onChange={(e) => handleInputChange('zeptoSenderEmail', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Must be verified in ZeptoMail
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sender Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="MyCinemas"
                        value={settings.zeptoSenderName}
                        onChange={(e) => handleInputChange('zeptoSenderName', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mb-3">Connection Test</h6>

<Row className="mb-3">
  <Col md={8}>
    <Form.Group>
      <Form.Label>Test Email Address</Form.Label>
      <Form.Control
        type="email"
        placeholder="Enter email to send test mail"
        value={testEmail}
        onChange={(e) => setTestEmail(e.target.value)}
      />
    </Form.Group>
  </Col>
</Row>

<div className="d-flex gap-2">
  <Button
    variant="outline-primary"
    onClick={handleTestConnection}
    disabled={loadingConnection}
  >
    <i className="bi-plug me-1"></i>
    {loadingConnection ? 'Testing...' : 'Test Connection'}
  </Button>

  <Button
    variant="outline-info"
    onClick={handleSendTestEmail}
    disabled={!testEmail || loadingEmail}
  >
    <i className="bi-envelope me-1"></i>
    {loadingEmail ? 'Sending...' : 'Send Test Email'}
  </Button>
</div>
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="sms" title="SMS Settings">
  <Card className="mt-3">
    <Card.Header>
      <h5 className="mb-0">SMS Configuration</h5>
    </Card.Header>
    <Card.Body>

      <Alert variant="info">
        <i className="bi bi-shield-lock me-2"></i>
        <strong>Security Notice:</strong> SMS API credentials are stored securely and not exposed publicly.
      </Alert>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>SMS API Key</Form.Label>
            <Form.Control
              type="password"  // 👈 IMPORTANT
              placeholder="Enter SMS API Key"
              value={settings.smsApiKey || ''}
              onChange={(e) => handleInputChange('smsApiKey', e.target.value)}
            />
            <Form.Text className="text-muted">
              This key is used by backend to send SMS notifications.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>


    </Card.Body>
  </Card>
</Tab>
<Tab eventKey="whatsapp" title="Whatsapp Settings">
  <Card className="mt-3">
    <Card.Header>
      <h5 className="mb-0">WhatsApp Configuration</h5>
    </Card.Header>
    <Card.Body>

      <Alert variant="info">
        <i className="bi bi-shield-lock me-2"></i>
        <strong>Security Notice:</strong> WhatsApp API credentials are stored securely and not exposed publicly.
      </Alert>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>WhatsApp API Key</Form.Label>
            <Form.Control
              type="password"  // 👈 IMPORTANT
              placeholder="Enter WhatsApp API Key"
              value={settings.whatsappApiKey || ''}
              onChange={(e) => handleInputChange('whatsappApiKey', e.target.value)}
            />
            <Form.Text className="text-muted">
              This key is used by backend to send WhatsApp notifications.
            </Form.Text>
          </Form.Group>
        </Col>
        
      </Row>


    </Card.Body>
  </Card>
</Tab>
        </Tabs>
      </div>
    </>
  );
};

export default Settings;
